import { ToastDismissedEvent, ToastDisplayedEvent } from "@qubit/autoparts";
import mixpanel from "mixpanel-browser";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { store } from "~/app/store";
import envConstants from "~/config/envConstants";
import { GetStatusCountsResponse, VariantFrontendDto } from "~/types/api";

type mixpanelActionType =
  | "Login"
  | "Logout"
  | "Button Click"
  | "Select Row"
  | "Unselect Row"
  | "Select Search Result"
  | "Search"
  | "Scan"
  | "PTL"
  | "Status Change"
  | "Input Change"
  | "Select Option"
  | "Modal Dismiss"
  | "Toast Dismiss"
  | "Analytics Dashboard Interaction"
  | "Error";

export type mixpanelActionPayload = {
  trackedPageName: string;
  type: mixpanelActionType;
  label: string;
  eventProperties?: unknown;
};

export const mixpanelInit = () => {
  // only log mixpanel events in production
  if (envConstants.RUNTIME_ENVIRONMENT !== "production") return;
  try {
    mixpanel.init("9e861e808460a3125424233f149ddf4a", {
      debug: true,
      track_pageview: true,
      persistence: "localStorage"
    });
  } catch (error) {
    // Ensure that mixpanel failures do not impact the software
    console.log({ message: "Mixpanel error", error });
  }
};

export const mixpanelLogout = () => {
  // only log mixpanel events in production
  if (envConstants.RUNTIME_ENVIRONMENT !== "production") {
    return;
  }
  try {
    mixpanel.track("Logout");
    mixpanel.reset();
  } catch (error) {
    // Ensure that mixpanel failures do not impact the software
    console.log({ message: "Mixpanel error", error });
  }
};

export function resolveMixpanelClientName(
  clientLoginPath: string | undefined
): string | undefined {
  const fromLoginPath = clientLoginPath?.replace("/login/", "");
  return fromLoginPath ?? undefined;
}

export const mixpanelTrack = (payload: mixpanelActionPayload) => {
  // only log mixpanel events in production
  if (envConstants.RUNTIME_ENVIRONMENT !== "production") {
    return;
  }
  try {
    const state = store.getState();
    const clientLoginPath = state?.login?.profile?.login_path || "undefined";
    mixpanel.track(payload.type, {
      ClientLoginPath: clientLoginPath,
      ClientName: resolveMixpanelClientName(clientLoginPath),
      FcName: state?.store?.usersFulfillmentCenter?.name || "undefined",
      FcAbbv: state?.store?.usersFulfillmentCenter?.abbreviation || "undefined",
      GridName:
        state?.workstations?.siteWorkstation?.autostoreGridName || "undefined",
      Page: payload.trackedPageName,
      Label: payload.label,
      Properties: payload.eventProperties,
      Environment: envConstants.RUNTIME_ENVIRONMENT
    });
  } catch (error) {
    // Ensure that mixpanel failures do not impact the software
    console.log({ message: "Mixpanel error", error });
  }
};

type ModalDismissReason = "backdropClick" | "escapeKeyDown";

/**
 * Track modal dismiss events (backdrop click or escape key)
 * @param trackedModalName - The modal name from useMixpanelModalName hook
 * @param reason - The MUI Dialog close reason ("backdropClick" or "escapeKeyDown")
 * @param eventProperties - Optional additional properties to track
 */
export const mixpanelTrackModalDismiss = (
  trackedModalName: string,
  reason: ModalDismissReason,
  eventProperties?: Record<string, unknown>
) => {
  mixpanelTrack({
    trackedPageName: trackedModalName,
    type: "Modal Dismiss",
    label: reason === "backdropClick" ? "Backdrop Click" : "Escape Key",
    ...(eventProperties ? { eventProperties } : {})
  });
};

export const mixpanelStartPickingWrapper = (
  trackedPageName: string,
  typeValue: mixpanelActionType,
  labelValue: string,
  batchCount: GetStatusCountsResponse | null | undefined
) => {
  const trackingPayload: mixpanelActionPayload = {
    trackedPageName,
    type: typeValue,
    label: labelValue,
    eventProperties: batchCount
      ? {
          "Cart Ready": batchCount["cartReady"],
          Scheduled: batchCount["scheduled"],
          Suspended: batchCount["suspended"],
          Dropped: batchCount["dropped"]
        }
      : {}
  };
  mixpanelTrack(trackingPayload);
};

export const mixpanelPickingConfirmCartWrapper = (
  trackedPageName: string,
  typeValue: mixpanelActionType,
  labelValue: string,
  values: {
    cartNumber: string | null;
    preppedCartNumber: string | null;
  }
) => {
  const trackingPayload: mixpanelActionPayload = {
    trackedPageName,
    type: typeValue,
    label: labelValue,
    eventProperties: {
      "Scanned Cart": values.cartNumber || "",
      "Prepped Cart": values.preppedCartNumber || ""
    }
  };
  mixpanelTrack(trackingPayload);
};

export const mixpanelTrackPickWrapper = (
  trackedPageName: string,
  typeValue: mixpanelActionType,
  labelValue: string,
  value: number | undefined
) => {
  const trackingPayload: mixpanelActionPayload = {
    trackedPageName,
    type: typeValue,
    label: labelValue,
    eventProperties: { "Tote Position": value || 0 }
  };
  mixpanelTrack(trackingPayload);
};

export const mixpanelSetHoldConfirm = (
  trackedPageName: string,
  typeValue: mixpanelActionType,
  labelValue: string,
  value: string,
  compartmentNumber?: number | string,
  binNumber?: number
) => {
  const trackingPayload: mixpanelActionPayload = {
    trackedPageName,
    type: typeValue,
    label: labelValue,
    eventProperties: {
      "Reason Type": value,
      Compartment: compartmentNumber || "N/A",
      "Bin Number": binNumber || "N/A"
    }
  };
  mixpanelTrack(trackingPayload);
};

type PageName =
  | "Unknown Page"
  | "Organization Login"
  | "Autostore Picking"
  | "Autostore Main"
  | "Autostore Inventory"
  | "Inventory Holds V2"
  | "Cycle Counts V2"
  | "Cycle Count Task V2"
  | "Induction V2"
  | "Manual Ops"
  | "Manual Inventory"
  | "Inventory Holds V1"
  | "Induction V1"
  | "Cycle Counts V1"
  | "Bin Maintenance V1"
  | "Bin Maintenance V2"
  | "Start Bin Reconfiguration"
  | "Bin Reconfiguration"
  | "Escalated Bins"
  | "Settings"
  | "Dev Tools"
  | "Service Support"
  | "Analytics"
  | "Orders"
  | "Batches"
  | "Create Batches"
  | "Carts"
  | "Manual Putaway"
  | "Manual Inventory Holds"
  | "Manual Cycle Count"
  | "Manual Picking"
  | "Cart Prep"
  | "Pick Tasks"
  | "Stage Totes"
  | "Ship Orders"
  | "Ship Consolidation";

// Cache for getPageNameForPath results
const pageNameCache = new Map<string, PageName>();

export const getPageNameForPath = (pathname: string): PageName => {
  const cached = pageNameCache.get(pathname);
  if (cached !== undefined) {
    return cached;
  }

  let pageName: PageName;

  switch (true) {
    case pathname.includes("/login"):
      pageName = "Organization Login";
      break;
    case pathname.includes("/autostore-pick"):
      pageName = "Autostore Picking";
      break;
    case pathname.includes("/autostore-main"):
      pageName = "Autostore Main";
      break;
    case pathname.includes("/autostore-inventory/holds"):
      pageName = "Inventory Holds V2";
      break;
    case pathname.includes("/autostore-inventory-holds"):
      pageName = "Inventory Holds V1";
      break;
    case pathname.includes("/autostore-inventory"):
      pageName = "Autostore Inventory";
      break;
    case pathname.includes("/autostore-cyclecountv2/cycle-count-task"):
      pageName = "Cycle Count Task V2";
      break;
    case pathname.includes("/autostore-cyclecountv2"):
      pageName = "Cycle Counts V2";
      break;
    case pathname.includes("/autostore-putaway-v2"):
      pageName = "Induction V2";
      break;
    case pathname.includes("autostore-putaway"):
      pageName = "Induction V1";
      break;
    case pathname.includes("/autostore-cyclecount"):
      pageName = "Cycle Counts V1";
      break;
    case pathname.includes("/workstation-bin-maintenance"):
      pageName = "Bin Maintenance V2";
      break;
    case pathname.includes("/bin-maintenance"):
      pageName = "Bin Maintenance V1";
      break;
    case pathname.includes("/start-bin-reconfiguration"):
      pageName = "Start Bin Reconfiguration";
      break;
    case pathname.includes("/bin-reconfiguration"):
      pageName = "Bin Reconfiguration";
      break;
    case pathname.includes("/escalated-bins"):
      pageName = "Escalated Bins";
      break;
    case pathname.includes("/settings"):
      pageName = "Settings";
      break;
    case pathname.includes("/dev-tools"):
      pageName = "Dev Tools";
      break;
    case pathname.includes("/service-support"):
      pageName = "Service Support";
      break;
    case pathname.includes("/analytics"):
      pageName = "Analytics";
      break;
    case pathname.includes("/admin-batches"):
      pageName = "Batches";
      break;
    case pathname.includes("/create-batches"):
      pageName = "Create Batches";
      break;
    case pathname.includes("/carts"):
      pageName = "Carts";
      break;
    case pathname.includes("/orders"):
      pageName = "Orders";
      break;
    case pathname.includes("/manual-inventory"):
      pageName = "Manual Inventory";
      break;
    case pathname.includes("/putaway"):
      pageName = "Manual Putaway";
      break;
    case pathname.includes("/inventory-holds"):
      pageName = "Manual Inventory Holds";
      break;
    case pathname.includes("/cycle-count"):
      pageName = "Manual Cycle Count";
      break;
    case pathname.includes("/pick-batches"):
      pageName = "Manual Picking";
      break;
    case /^\/batches\/[^/]+\/cart-prep$/.test(pathname):
      pageName = "Cart Prep";
      break;
    case /^\/batches\/[^/]+\/pick$/.test(pathname):
      pageName = "Pick Tasks";
      break;
    case /^\/batches\/[^/]+\/stage$/.test(pathname):
      pageName = "Stage Totes";
      break;
    case pathname.includes("/ship/order"):
      pageName = "Ship Orders";
      break;
    case pathname.includes("/ship/consolidation"):
      pageName = "Ship Consolidation";
      break;
    default:
      // Handle root path "/" for Manual Ops
      if (pathname === "/") {
        pageName = "Manual Ops";
      } else {
        pageName = "Unknown Page";
      }
  }

  pageNameCache.set(pathname, pageName);
  return pageName;
};

/**
 * Hook that returns the page name for mixpanel tracking
 */
export function useMixpanelPageName() {
  const { pathname } = useLocation();
  const view = getPageNameForPath(pathname);

  return view;
}

/**
 * Returns tracked modal name: "Page Name: Modal Name Modal"
 * @param modalName - Modal name without "Modal" suffix (e.g., "Add Tote", "Navigate Away")
 */
export function useMixpanelModalName(modalName: string) {
  const pageName = useMixpanelPageName();
  return useMemo(
    () => `${pageName}: ${modalName} Modal`,
    [pageName, modalName]
  );
}

/**
 * Returns tracked context name: "Page Name: Context Name"
 * Use for non-modal UI elements (menus, drawers, panels, etc.)
 * @param contextName - UI context name (e.g., "User Menu", "Navigation Drawer")
 */
export function useMixpanelContextName(contextName: string) {
  const pageName = useMixpanelPageName();
  return useMemo(() => `${pageName}: ${contextName}`, [pageName, contextName]);
}

/**
 * Tracks navigation drawer button clicks
 * @param pageUrl - Destination URL
 * @param trackedPageName - Current page name
 */
export function mixpanelTrackNavigationDrawerWrapper(
  pageUrl: string,
  trackedPageName: string
) {
  const destinationPageName = getPageNameForPath(pageUrl);
  mixpanelTrack({
    trackedPageName: `${trackedPageName}: Navigation Drawer`,
    type: "Button Click",
    label: `Navigate to ${destinationPageName}`,
    eventProperties: {
      "Destination URL": pageUrl
    }
  });
}

/**
 * Formats toast event data for Mixpanel tracking.
 * Handles both error toast displayed and toast dismissed events.
 */
export function formatToastTrackingEvent(
  event:
    | { type: "displayed"; data: ToastDisplayedEvent }
    | { type: "dismissed"; data: ToastDismissedEvent }
): mixpanelActionPayload {
  const currentPageName = getPageNameForPath(window.location.pathname);
  const locationName = event.data.trackingLocation || currentPageName;

  if (event.type === "displayed") {
    const { title, description = "", duplicateCount } = event.data;
    return {
      trackedPageName: locationName,
      type: "Error",
      label: title,
      eventProperties: {
        "Toast Description": description,
        "Toast Duplicate Count": duplicateCount || 1
      }
    };
  }

  // type === "dismissed"
  const { title, severity } = event.data;
  return {
    trackedPageName: locationName,
    type: "Toast Dismiss",
    label: "Close Toast",
    eventProperties: {
      "Toast Title": title,
      "Toast Severity": severity
    }
  };
}

/**
 * Track error toast displayed events (errors only)
 */
export function mixpanelTrackErrorToastDisplayed(event: ToastDisplayedEvent) {
  if (event.severity === "error") {
    mixpanelTrack(formatToastTrackingEvent({ type: "displayed", data: event }));
  }
}

/**
 * Track toast dismissed by user (all severities included, but only if user clicks the close icon button - not auto-dismiss)
 */
export function mixpanelTrackToastDismissed(event: ToastDismissedEvent) {
  mixpanelTrack(formatToastTrackingEvent({ type: "dismissed", data: event }));
}

// Product fields sent with cycle-count selection Mixpanel event properties
export type MixpanelCycleCountProductRow = {
  "Product Name": string;
  SKU: string;
};

//Maps a variant (or undefined) to Mixpanel cycle-count product fields.
export function formatCycleCountProductMixpanelRowFromVariant(
  variant: VariantFrontendDto | undefined
): MixpanelCycleCountProductRow {
  return {
    "Product Name": variant?.productName ?? "",
    SKU: variant?.sku ?? ""
  };
}

//Resolves `variantId` in `variants` to Mixpanel row fields (e.g. table deselect).

export function formatCycleCountProductMixpanelRowForVariantInList(
  variantId: string,
  variants: VariantFrontendDto[]
): MixpanelCycleCountProductRow {
  return formatCycleCountProductMixpanelRowFromVariant(
    variants.find((v) => v.variantId === variantId)
  );
}

//One Mixpanel row per selected table row (e.g. Start counting).
export function formatCycleCountProductMixpanelRowsForTableSelection(
  selectedRows: { variantId: string }[],
  variants: VariantFrontendDto[]
): MixpanelCycleCountProductRow[] {
  return selectedRows.map((r) =>
    formatCycleCountProductMixpanelRowForVariantInList(r.variantId, variants)
  );
}

/** Inventory table filters (hold type/temp zone) */
export function formatInventoryNavbarFilterSelectionMixpanelPayload(
  trackedPageName: string,
  filterNames: string[]
): mixpanelActionPayload {
  return {
    trackedPageName,
    type: "Input Change",
    label: "Inventory filter selection",
    eventProperties: {
      "Filter count": filterNames.length,
      Filters: filterNames
    }
  };
}
